import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import api from "../../../utils/api";
import ExcelJS from "exceljs";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { StackedBarChart, StatCard } from "./components/V5Charts";
import ActiveUnitsHistorySection from "../../../components/dashboards/ActiveUnitsHistorySection";
import AltasBajasPlatformCards from "../../../components/dashboards/AltasBajasPlatformCards";
import PlatformAlerts from "../../../components/PlatformAlerts";
import {
  usePlatformCriticalAlerts,
  useReportingSummary,
  useReportingCompanies,
  computeRiskDistribution,
} from "../../../features/dashboards/reporting";
import ReportingUnitsTables from "../../../components/dashboards/ReportingUnitsTables";
import PlatformStatsCards from "../../../components/dashboards/PlatformStatsCards";
import AllClientsTable from "../../../components/dashboards/AllClientsTable";
import { fileDate } from "../../../features/sg/exportRows";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);
// ChartDataLabels NO se registra global: ChartJS.register aplica a todas las
// gráficas de la app (el router importa este módulo al arrancar) y dibujaba
// «x: …, y: …» sobre cada punto de los informes de Temperatura y Voltaje.

interface DailyHistoryData {
  date: string;
  active_units: number;
  altas_count: number;
  bajas_count: number;
}

interface StackedBarData {
  platform: string;
  totalUnits: number;
  healthy: { averagePercentage: number; companyCount: number; units: number };
  warning: { averagePercentage: number; companyCount: number; units: number };
  highRisk: { averagePercentage: number; companyCount: number; units: number };
  critical: { averagePercentage: number; companyCount: number; units: number };
}

export default function V5Dashboard() {
  const { t } = useTranslation("entregaServicio");
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);

  const [historyData, setHistoryData] = useState<DailyHistoryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDownloadingMonthly, setIsDownloadingMonthly] = useState(false);
  const [devicesCount, setDevicesCount] = useState(0);
  const [devicesLoading, setDevicesLoading] = useState(true);

  const v5SummaryQuery = useReportingSummary({ platform: "v5" });
  const v5Sum = v5SummaryQuery.data?.by_platform?.v5;
  const v5CompaniesQuery = useReportingCompanies({ platform: "v5" });

  const v5Dist = computeRiskDistribution(v5CompaniesQuery.data ?? []);
  const totalV5Companies =
    v5Dist.buckets.healthy.companyCount +
      v5Dist.buckets.warning.companyCount +
      v5Dist.buckets.highRisk.companyCount +
      v5Dist.buckets.critical.companyCount || 1;
  const v5Cat = (b: "healthy" | "warning" | "highRisk" | "critical") => ({
    averagePercentage:
      Math.round((v5Dist.buckets[b].companyCount / totalV5Companies) * 1000) /
      10,
    companyCount: v5Dist.buckets[b].companyCount,
    units: v5Dist.buckets[b].units,
  });
  const stackedBarData: StackedBarData[] = [
    {
      platform: "V5",
      totalUnits: v5Sum?.total ?? v5Dist.totalUnits,
      healthy: v5Cat("healthy"),
      warning: v5Cat("warning"),
      highRisk: v5Cat("highRisk"),
      critical: v5Cat("critical"),
    },
  ];
  const reportingDataLoading = v5CompaniesQuery.isLoading;
  const statsLoading = devicesLoading || v5SummaryQuery.isLoading;
  const statsData = {
    users: v5Sum?.users ?? 0,
    devices: devicesCount,
    vehicles: v5Sum?.total ?? 0,
    companies: v5Sum?.companies ?? 0,
  };

  const criticalAlerts = usePlatformCriticalAlerts("v5");
  const criticalCompanies = criticalAlerts.data?.companies ?? [];
  const allUnits = criticalAlerts.data?.units ?? [];

  // Dispositivos: conteo dedicado (la API de reporting solo expone "unidades")
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/devicesListForReports", {
          params: { limit: 1, page: 1 },
          _skipCompanyScope: true,
        });
        const count =
          res.data?.meta?.totalItems ??
          res.data?.data?.meta?.totalItems ??
          res.data?.totalItems ??
          0;
        if (!cancelled) setDevicesCount(count);
      } catch {
        if (!cancelled) setDevicesCount(0);
      } finally {
        if (!cancelled) setDevicesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch historical data
  useEffect(() => {
    const fetchHistoryData = async () => {
      try {
        const response = await api.get("/dailyHistory/monthly-data");
        const data = response.data.data || [];
        setHistoryData(data);
      } catch (error) {
        console.error("[V5Dashboard] Error fetching history data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHistoryData();
  }, []);

  // Download monthly Excel
  const handleDownloadMonthlyExcel = async () => {
    if (isDownloadingMonthly) return;
    setIsDownloadingMonthly(true);
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Dumax";
      workbook.created = new Date();

      const numDays = historyData.length;
      const totalCols = numDays + 1;

      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth();
      const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
      const dayNames = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

      // Helper: same tick logic as SVG charts
      const getYAxisMax = (max: number, stepUnit: number) => {
        const step = Math.ceil(max / 3 / stepUnit) * stepUnit || stepUnit;
        return step * 3;
      };

      // Generate active units chart
      const activeUnitsCanvas = document.createElement("canvas");
      activeUnitsCanvas.width = 1600;
      activeUnitsCanvas.height = 800;
      const activeUnitsCtx = activeUnitsCanvas.getContext("2d")!;

      const activeUnitsMaxData = Math.max(...historyData.map((d) => d.active_units), 1);
      const activeUnitsYMax = getYAxisMax(activeUnitsMaxData, 50);
      
      const activeUnitsChart = new ChartJS(activeUnitsCtx, {
        plugins: [ChartDataLabels],
        type: "bar",
        data: {
          labels: historyData.map((d) => new Date(d.date).getDate()),
          datasets: [{
            label: "Unidades Activas",
            data: historyData.map((d) => d.active_units),
            backgroundColor: "#22c55e",
          }],
        },
        options: {
          animation: false,
          responsive: false,
          plugins: {
            legend: {
              display: true,
              labels: {
                color: "#000000",
                font: { size: 18, weight: "bold" },
              },
            },
            datalabels: {
              display: true,
              color: "#000000",
              anchor: "end",
              align: "top",
              font: {
                size: 14,
                weight: "bold",
              },
              formatter: (value: number) => value,
            },
          },
          scales: {
            x: {
              ticks: { color: "#000000", font: { size: 16 } },
              grid: { color: "#E5E7EB" },
            },
            y: {
              beginAtZero: true,
              max: activeUnitsYMax,
              ticks: { color: "#000000", font: { size: 16 } },
              grid: { color: "#E5E7EB" },
            },
          },
        },
      });

      const activeUnitsImage = activeUnitsCanvas.toDataURL("image/png", 1.0);
      activeUnitsChart.destroy();

      // Generate altas/bajas chart
      const altasBajasCanvas = document.createElement("canvas");
      altasBajasCanvas.width = 1600;
      altasBajasCanvas.height = 800;
      const altasBajasCtx = altasBajasCanvas.getContext("2d")!;

      const altasBajasMaxData = Math.max(...historyData.map((d) => d.altas_count), ...historyData.map((d) => d.bajas_count), 1);
      const altasBajasYMax = getYAxisMax(altasBajasMaxData, 50);
      
      const altasBajasChart = new ChartJS(altasBajasCtx, {
        plugins: [ChartDataLabels],
        type: "line",
        data: {
          labels: historyData.map((d) => new Date(d.date).getDate()),
          datasets: [
            {
              label: "Altas",
              data: historyData.map((d) => d.altas_count),
              borderColor: "#22c55e",
              backgroundColor: "#22c55e",
              tension: 0.1,
              pointRadius: 6,
              pointBackgroundColor: "#22c55e",
            },
            {
              label: "Bajas",
              data: historyData.map((d) => d.bajas_count),
              borderColor: "#ef4444",
              backgroundColor: "#ef4444",
              tension: 0.1,
              pointRadius: 6,
              pointBackgroundColor: "#ef4444",
            },
          ],
        },
        options: {
          animation: false,
          responsive: false,
          plugins: {
            legend: {
              display: true,
              labels: {
                color: "#000000",
                font: { size: 18, weight: "bold" },
              },
            },
            datalabels: {
              display: true,
              color: "#000000",
              anchor: "end",
              align: "top",
              font: {
                size: 14,
                weight: "bold",
              },
              formatter: (value: number) => value,
            },
          },
          scales: {
            x: {
              ticks: { color: "#000000", font: { size: 16 } },
              grid: { color: "#E5E7EB" },
            },
            y: {
              beginAtZero: true,
              max: altasBajasYMax,
              ticks: { color: "#000000", font: { size: 16 } },
              grid: { color: "#E5E7EB" },
            },
          },
        },
      });

      const altasBajasImage = altasBajasCanvas.toDataURL("image/png", 1.0);
      altasBajasChart.destroy();

      // Add chart images to workbook
      const activeUnitsImageId = workbook.addImage({
        base64: activeUnitsImage.replace(/^data:image\/png;base64,/, ""),
        extension: "png",
      });

      const altasBajasImageId = workbook.addImage({
        base64: altasBajasImage.replace(/^data:image\/png;base64,/, ""),
        extension: "png",
      });

      // Create single sheet
      const ws = workbook.addWorksheet("Reporte Mensual", {
        views: [{ showGridLines: false }],
      });

      const firstDate = new Date(historyData[0].date).toISOString().slice(0, 10);
      const lastDate = new Date(historyData[historyData.length - 1].date).toISOString().slice(0, 10);

      // Title
      const titleRowNum = 1;
      ws.mergeCells(titleRowNum, 1, titleRowNum, totalCols);
      const titleCell = ws.getCell(titleRowNum, 1);
      titleCell.value = `Reporte Mensual — ${monthNames[month]} ${year}`;
      titleCell.font = { bold: true, size: 16, color: { argb: "FF22C55E" } };
      titleCell.alignment = { horizontal: "center" };

      // Subtitle
      const subtitleRowNum = titleRowNum + 1;
      ws.mergeCells(subtitleRowNum, 1, subtitleRowNum, totalCols);
      const subtitleCell = ws.getCell(subtitleRowNum, 1);
      subtitleCell.value = `Periodo: ${firstDate} al ${lastDate}`;
      subtitleCell.font = { italic: true, size: 10, color: { argb: "FF6B7280" } };
      subtitleCell.alignment = { horizontal: "center" };

      // === Unidades Activas Section ===
      const sectionTitleRowNum1 = subtitleRowNum + 2;
      ws.mergeCells(sectionTitleRowNum1, 1, sectionTitleRowNum1, totalCols);
      const sectionTitle1 = ws.getCell(sectionTitleRowNum1, 1);
      sectionTitle1.value = "Unidades Activas";
      sectionTitle1.font = { bold: true, size: 14, color: { argb: "FF22C55E" } };
      sectionTitle1.alignment = { horizontal: "left" };

      const headerRowNum1 = sectionTitleRowNum1 + 1;
      const headerRow1 = ws.getRow(headerRowNum1);
      headerRow1.getCell(1).value = "Días";
      historyData.forEach((d, i) => {
        headerRow1.getCell(i + 2).value = new Date(d.date).getDate();
      });

      const dowRowNum1 = headerRowNum1 + 1;
      const dowRow1 = ws.getRow(dowRowNum1);
      dowRow1.getCell(1).value = "Día";
      historyData.forEach((d, i) => {
        dowRow1.getCell(i + 2).value = dayNames[new Date(d.date).getDay()];
      });

      const dataRowNum1 = dowRowNum1 + 1;
      const dataRow1 = ws.getRow(dataRowNum1);
      dataRow1.getCell(1).value = "Señales facturables";
      historyData.forEach((d, i) => {
        dataRow1.getCell(i + 2).value = d.active_units;
      });

      [headerRow1, dowRow1, dataRow1].forEach((row) => {
        row.font = { size: 10 };
        row.alignment = { horizontal: "center" };
      });

      headerRow1.font = { bold: true, size: 10, color: { argb: "FFFFFFFF" } };
      headerRow1.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF22C55E" } };
      dowRow1.font = { italic: true, size: 9, color: { argb: "FF6B7280" } };
      dataRow1.font = { bold: true, size: 11, color: { argb: "FF22C55E" } };

      const chartRowNum1 = dataRowNum1 + 2;
      ws.addImage(activeUnitsImageId, {
        tl: { col: 0, row: chartRowNum1 },
        ext: { width: 800, height: 400 },
      });

      // === Altas y Bajas Section ===
      const sectionTitleRowNum2 = chartRowNum1 + 27;
      ws.mergeCells(sectionTitleRowNum2, 1, sectionTitleRowNum2, totalCols);
      const sectionTitle2 = ws.getCell(sectionTitleRowNum2, 1);
      sectionTitle2.value = "Altas y Bajas";
      sectionTitle2.font = { bold: true, size: 14, color: { argb: "FF22C55E" } };
      sectionTitle2.alignment = { horizontal: "left" };

      const headerRowNum2 = sectionTitleRowNum2 + 1;
      const headerRow2 = ws.getRow(headerRowNum2);
      headerRow2.getCell(1).value = "Días";
      historyData.forEach((d, i) => {
        headerRow2.getCell(i + 2).value = new Date(d.date).getDate();
      });

      const dowRowNum2 = headerRowNum2 + 1;
      const dowRow2 = ws.getRow(dowRowNum2);
      dowRow2.getCell(1).value = "Día";
      historyData.forEach((d, i) => {
        dowRow2.getCell(i + 2).value = dayNames[new Date(d.date).getDay()];
      });

      const altasRowNum2 = dowRowNum2 + 1;
      const altasRow2 = ws.getRow(altasRowNum2);
      altasRow2.getCell(1).value = "Altas";
      historyData.forEach((d, i) => {
        altasRow2.getCell(i + 2).value = d.altas_count;
      });

      const bajasRowNum2 = altasRowNum2 + 1;
      const bajasRow2 = ws.getRow(bajasRowNum2);
      bajasRow2.getCell(1).value = "Bajas";
      historyData.forEach((d, i) => {
        bajasRow2.getCell(i + 2).value = d.bajas_count;
      });

      [headerRow2, dowRow2, altasRow2, bajasRow2].forEach((row) => {
        row.font = { size: 10 };
        row.alignment = { horizontal: "center" };
      });

      headerRow2.font = { bold: true, size: 10, color: { argb: "FFFFFFFF" } };
      headerRow2.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF22C55E" } };
      dowRow2.font = { italic: true, size: 9, color: { argb: "FF6B7280" } };
      altasRow2.font = { bold: true, size: 11, color: { argb: "FF22C55E" } };
      bajasRow2.font = { bold: true, size: 11, color: { argb: "FFEF4444" } };

      for (let c = 2; c <= totalCols; c++) {
        altasRow2.getCell(c).font = { bold: true, color: { argb: "FF22C55E" } };
        bajasRow2.getCell(c).font = { bold: true, color: { argb: "FFEF4444" } };
      }

      ws.getColumn(1).width = 24;
      for (let c = 2; c <= totalCols; c++) {
        ws.getColumn(c).width = 6;
      }

      const chartRowNum2 = bajasRowNum2 + 2;
      ws.addImage(altasBajasImageId, {
        tl: { col: 0, row: chartRowNum2 },
        ext: { width: 800, height: 400 },
      });

      // Generate and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const todayStr = fileDate(today);
      const fileName = `senales-dumax-mensual_${todayStr}.xlsx`;
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("[V5Dashboard] Error generando reporte:", error);
    } finally {
      setIsDownloadingMonthly(false);
    }
  };

  return (
    <div className="w-full h-full overflow-auto p-4 sm:p-6">
      <div className="mb-6 sm:mb-8">
        <h1 className={`text-2xl sm:text-3xl font-bold ${themeClasses.text}`}>
          Dashboard V5
        </h1>
        <p className={`text-sm ${themeClasses.textSecondary}`}>
          Vista general de la plataforma V5
        </p>
      </div>

      {/* Stats normalizados (API nueva) */}
      <div className="mb-6 sm:mb-8">
        <PlatformStatsCards platform="v5" themeClasses={themeClasses} />
      </div>

      {/* Stats Cards (legacy, oculto) */}
      {false && (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <StatCard
          icon={<i className="ri-user-line text-white text-sm"></i>}
          label="Usuarios"
          value={statsLoading ? "..." : statsData.users}
          sublabel="Total"
          iconBgColor="bg-blue-500"
          themeClasses={themeClasses}
        />
        <StatCard
          icon={<i className="ri-device-line text-white text-sm"></i>}
          label="Dispositivos"
          value={statsLoading ? "..." : statsData.devices}
          sublabel="Total"
          iconBgColor="bg-purple-500"
          themeClasses={themeClasses}
        />
        <StatCard
          icon={<i className="ri-truck-line text-white text-sm"></i>}
          label="Vehículos"
          value={statsLoading ? "..." : statsData.vehicles}
          sublabel="Total"
          iconBgColor="bg-orange-500"
          themeClasses={themeClasses}
        />
        <StatCard
          icon={<i className="ri-building-line text-white text-sm"></i>}
          label="Compañías"
          value={statsLoading ? "..." : statsData.companies}
          sublabel="Total"
          iconBgColor="bg-indigo-500"
          themeClasses={themeClasses}
        />
      </div>
      )}

      {/* Sección: Historial Mensual de Unidades — movida a Resumen */}
      {false && (
      <div className="space-y-6">
        <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
            <div>
              <h3 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
                Historial Mensual de Unidades
              </h3>
              <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
                Unidades activas, altas y bajas del mes actual
              </p>
            </div>
            <button
              onClick={handleDownloadMonthlyExcel}
              disabled={isDownloadingMonthly}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isDownloadingMonthly
                  ? "bg-primary-500/60 text-gray-900 cursor-not-allowed"
                  : "bg-primary-500 text-gray-900 hover:bg-primary-400"
              }`}
              title="Descargar reporte mensual con gráficas"
            >
              {isDownloadingMonthly ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Generando…
                </>
              ) : (
                <>
                  <i className="ri-file-excel-2-line" />
                  Exportar Excel
                </>
              )}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-6">
            <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6 h-96 animate-pulse`}></div>
            <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6 h-96 animate-pulse`}></div>
          </div>
        ) : (
          <div className="space-y-6">
            <ActiveUnitsHistorySection
              themeClasses={themeClasses}
              platform="v5"
              platformLabel="V5"
            />
          </div>
        )}
      </div>
      )}

      <div className="mt-6 sm:mt-8">
        <ActiveUnitsHistorySection
          themeClasses={themeClasses}
          platform="v5"
          platformLabel="V5"
          showActiveUnits={false}
        />
      </div>

      <div className="mt-6 sm:mt-8">
        <AltasBajasPlatformCards themeClasses={themeClasses} platform="v5" />
      </div>

      {/* Sección: Distribución de riesgo por cliente */}
      <div className="mt-6 sm:mt-8">
        <div className="mb-3 sm:mb-4">
          <h2 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
            Distribución de riesgo por cliente
          </h2>
          <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            Análisis de riesgo por cliente en la plataforma V5
          </p>
        </div>

        {reportingDataLoading ? (
          <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6 h-64 animate-pulse`}></div>
        ) : (
          <StackedBarChart data={stackedBarData} themeClasses={themeClasses} t={t} />
        )}
      </div>

      {/* Reportan / No reportan (API nueva) */}
      <div className="mt-6 sm:mt-8">
        <ReportingUnitsTables platform="v5" themeClasses={themeClasses} />
      </div>

      <div className="mt-6 sm:mt-8">
        <div className="mb-3 sm:mb-4">
          <h2 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
            Todos los clientes
          </h2>
          <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            Listado completo de clientes V5 con su categoría de servicio
          </p>
        </div>
        {reportingDataLoading ? (
          <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6 h-64 animate-pulse`}></div>
        ) : (
          <AllClientsTable
            platformName="V5"
            companies={v5CompaniesQuery.data ?? []}
            themeClasses={themeClasses}
          />
        )}
      </div>

      {/* Sección: Alertas Críticas */}
      <div className="mt-6 sm:mt-8">
        {criticalAlerts.isLoading ? (
          <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}>
            <div className="space-y-3">
              <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
              <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
              <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
            </div>
          </div>
        ) : (
          <PlatformAlerts
            companies={criticalCompanies}
            totalClients={v5CompaniesQuery.data?.length ?? 0}
            allUnits={allUnits}
            platformName="V5"
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
          />
        )}
      </div>
    </div>
  );
}